import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import MicIcon from '@mui/icons-material/Mic';
import Send from '@mui/icons-material/Send';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Recorder from 'opus-recorder';
import encoderPath from 'opus-recorder/dist/encoderWorker.min.js?url';
import { type FC, type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { usePostAudioMessageMutation } from 'services/api/messages';
import type { Message } from 'services/types/message';
import { NAVY, RADIUS_CARD, RADIUS_COIN, SHADOW_CARD, SURFACE_CONTAINER_LOW, YELLOW } from 'styles/tokens';

interface Props {
  clientId: number;
  /** Element the recording bar is anchored to (the chat footer): it opens right above it, same width. */
  anchorRef: RefObject<HTMLDivElement | null>;
  disabled?: boolean;
  onActiveChange?: (active: boolean) => void;
  onSent: (message: Message) => void;
}

const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const VoiceRecorder: FC<Props> = ({ clientId, anchorRef, disabled, onActiveChange, onSent }) => {
  const [status, setStatus] = useState<'idle' | 'recording' | 'recorded'>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<Recorder | null>(null);
  const blobRef = useRef<Blob | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [postAudioMutation, { isLoading: isSending }] = usePostAudioMessageMutation();

  const clearTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  // Stop the mic tracks and close the AudioContext so Chrome/macOS drop their
  // "recording" indicators. stop() alone releases the tracks, but discarding
  // mid-recording skips stop(), and the AudioContext survives it either way.
  const releaseRecorder = useCallback(() => {
    try {
      recorderRef.current?.close().catch(() => {});
    } catch {
      // close() throws if start() never finished (e.g. mic permission denied)
    }
    recorderRef.current = null;
  }, []);

  const reset = () => {
    clearTimer();
    releaseRecorder();
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    blobRef.current = null;
    setElapsed(0);
    setStatus('idle');
    onActiveChange?.(false);
  };

  // Clean up the object URL / timer if the component unmounts mid-recording.
  useEffect(
    () => () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    },
    [audioUrl]
  );

  // Release the mic if the component unmounts (e.g. navigating away mid-recording).
  useEffect(() => releaseRecorder, [releaseRecorder]);

  const startRecording = () => {
    setError(null);
    const recorder = new Recorder({ encoderPath, numberOfChannels: 1, encoderSampleRate: 48000 });
    recorder.ondataavailable = (data) => {
      const blob = new Blob([data as BlobPart], { type: 'audio/ogg' });
      blobRef.current = blob;
      setAudioUrl(URL.createObjectURL(blob));
      setStatus('recorded');
    };
    recorderRef.current = recorder;
    recorder
      .start()
      .then(() => {
        setStatus('recording');
        onActiveChange?.(true);
        setElapsed(0);
        timerRef.current = setInterval(() => setElapsed((prev) => prev + 1), 1000);
      })
      .catch((err) => {
        console.error('[VoiceRecorder] Could not start recording', err);
        setError('No se pudo acceder al micrófono');
        reset();
      });
  };

  const stopRecording = () => {
    clearTimer();
    recorderRef.current?.stop().catch((err) => console.error('[VoiceRecorder] stop error', err));
  };

  const handleSend = () => {
    if (!blobRef.current) return;
    postAudioMutation({ clientId, audio: blobRef.current })
      .unwrap()
      .then((message) => {
        onSent(message);
        reset();
      })
      .catch((err) => {
        console.error('[VoiceRecorder] send error', err);
        setError('No se pudo enviar el audio');
      });
  };

  return (
    <>
      <IconButton
        title="Grabar audio"
        onClick={startRecording}
        disabled={disabled || status !== 'idle'}
        sx={{ color: alpha(NAVY, 0.55) }}
      >
        <MicIcon />
      </IconButton>

      <Popover
        open={status !== 'idle'}
        anchorEl={anchorRef.current}
        // No onClose: clicking outside or pressing Escape neither stops the recording
        // nor discards the preview — only the trash, check and send buttons act on it.
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              mt: -1,
              p: 1.5,
              width: anchorRef.current ? anchorRef.current.clientWidth - 16 : undefined,
              borderRadius: RADIUS_CARD,
              boxShadow: SHADOW_CARD,
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {status === 'recording' && (
            <>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: '#E53935',
                  animation: 'voicePulse 1s ease-in-out infinite',
                  '@keyframes voicePulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.2 } },
                }}
              />
              <Typography variant="body2" sx={{ color: NAVY, fontWeight: 600, flexGrow: 1 }}>
                Grabando… {formatTime(elapsed)}
              </Typography>
              <IconButton title="Descartar" onClick={reset} sx={{ color: alpha(NAVY, 0.55) }}>
                <DeleteIcon />
              </IconButton>
              <IconButton
                title="Terminar grabación"
                onClick={stopRecording}
                sx={{
                  backgroundColor: YELLOW,
                  color: NAVY,
                  '&:hover': { backgroundColor: YELLOW, opacity: 0.92 },
                }}
              >
                <CheckIcon />
              </IconButton>
            </>
          )}

          {status === 'recorded' && audioUrl && (
            <>
              <IconButton title="Descartar" onClick={reset} sx={{ color: alpha(NAVY, 0.55) }}>
                <DeleteIcon />
              </IconButton>
              <Box
                component="audio"
                controls
                controlsList="nodownload noplaybackrate"
                src={audioUrl}
                sx={{
                  height: 40,
                  flexGrow: 1,
                  '&::-webkit-media-controls-mute-button': { display: 'none' },
                  '&::-webkit-media-controls-volume-slider': { display: 'none' },
                  '&::-webkit-media-controls-volume-control-container': { display: 'none' },
                }}
              />
              <IconButton
                title="Enviar audio"
                onClick={handleSend}
                disabled={isSending}
                sx={{
                  backgroundColor: YELLOW,
                  color: NAVY,
                  '&:hover': { backgroundColor: YELLOW, opacity: 0.92 },
                }}
              >
                {isSending ? <CircularProgress size={20} /> : <Send fontSize="small" />}
              </IconButton>
            </>
          )}
        </Box>
        {error && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 1,
              color: '#E53935',
              px: 0.5,
              borderRadius: RADIUS_COIN,
              bgcolor: SURFACE_CONTAINER_LOW,
            }}
          >
            {error}
          </Typography>
        )}
      </Popover>
    </>
  );
};

export default VoiceRecorder;
